import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, User, ChevronDown, Users, FileText, LogIn, UserPlus, Home, Gift, Music, Crown, Truck, Phone, Star } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../App";

const RAFHA_LOGO = "https://customer-assets.emergentagent.com/job_4c248649-c7ad-49ab-8962-bf67e4ed6b65/artifacts/rogqq55c_F1371478-432E-404F-80CC-D2C1450E0839.png";
const CMC_LOGO = "https://customer-assets.emergentagent.com/job_4c248649-c7ad-49ab-8962-bf67e4ed6b65/artifacts/33twacgu_IMG_0221.png";

const Navbar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [rafhaDropdownOpen, setRafhaDropdownOpen] = useState(false);
  const [choiceDropdownOpen, setChoiceDropdownOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/95 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <div className="relative">
              <div className="absolute inset-0 bg-gold/20 rounded-full blur-md"></div>
              <img 
                src={RAFHA_LOGO} 
                alt="Rafha Entertainment" 
                className="w-12 h-12 object-contain relative z-10 drop-shadow-[0_0_8px_rgba(212,175,55,0.3)]" 
              />
            </div>
            <span className="font-heading font-bold text-white text-xl hidden sm:block">Rafha</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* Home */}
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                location.pathname === '/' 
                  ? "text-gold" 
                  : "text-white/70 hover:text-white"
              }`}
            >
              Home
            </Link>
            
            {/* Rafha Entertainment Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setRafhaDropdownOpen(true)}
              onMouseLeave={() => setRafhaDropdownOpen(false)}
            >
              <button
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                  location.pathname.includes('/vendors') || location.pathname.includes('/vendor-application')
                    ? "text-gold" 
                    : "text-white/70 hover:text-white"
                }`}
                data-testid="nav-rafha-dropdown"
              >
                Rafha Entertainment
                <ChevronDown className={`w-4 h-4 transition-transform ${rafhaDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {rafhaDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-[#0A0A0A] border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                  <div className="p-2">
                    <Link
                      to="/vendors"
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
                      data-testid="nav-browse-vendors"
                    >
                      <Users className="w-4 h-4 text-gold" />
                      <span className="text-white text-sm">Browse Vendors</span>
                    </Link>
                    
                    <Link
                      to="/become-vendor"
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
                      data-testid="nav-become-vendor"
                    >
                      <Star className="w-4 h-4 text-gold" />
                      <span className="text-white text-sm">Become a Vendor</span>
                    </Link>
                    
                    <Link
                      to="/vendor-application"
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
                      data-testid="nav-vendor-application"
                    >
                      <FileText className="w-4 h-4 text-gold" />
                      <span className="text-white text-sm">Vendor Application</span>
                    </Link>
                    
                    {!user && (
                      <>
                        <Link
                          to="/auth"
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
                        >
                          <LogIn className="w-4 h-4 text-gold" />
                          <span className="text-white text-sm">Sign In</span>
                        </Link>
                        
                        <Link
                          to="/auth?mode=register"
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
                        >
                          <UserPlus className="w-4 h-4 text-gold" />
                          <span className="text-white text-sm">Sign Up</span>
                        </Link>
                      </>
                    )}
                    
                    {user && (
                      <Link
                        to="/dashboard"
                        className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
                      >
                        <User className="w-4 h-4 text-gold" />
                        <span className="text-white text-sm">Dashboard</span>
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Choice Mobile Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setChoiceDropdownOpen(true)}
              onMouseLeave={() => setChoiceDropdownOpen(false)}
            >
              <button
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                  location.pathname.includes('/choice-mobile') 
                    ? "text-gold" 
                    : "text-white/70 hover:text-white"
                }`}
                data-testid="nav-choice-dropdown"
              >
                Choice Mobile
                <ChevronDown className={`w-4 h-4 transition-transform ${choiceDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {choiceDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-[#0A0A0A] border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                  <div className="p-2">
                    <Link
                      to="/choice-mobile"
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
                      data-testid="nav-choice-home"
                    >
                      <Home className="w-4 h-4 text-gold" />
                      <span className="text-white text-sm">Choice Mobile Home</span>
                    </Link>
                    
                    <div className="border-t border-white/5 my-2"></div>
                    <div className="px-4 py-2">
                      <p className="text-white/40 text-xs uppercase tracking-wider">Celebration Packages</p>
                    </div>
                    
                    <Link
                      to="/choice-mobile"
                      className="flex items-center justify-between px-4 py-2.5 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Gift className="w-4 h-4 text-gold/70" />
                        <span className="text-white/80 text-sm">Celebration Pull-Up</span>
                      </div>
                      <span className="text-gold text-sm font-bold">$175</span>
                    </Link>
                    
                    <Link
                      to="/choice-mobile"
                      className="flex items-center justify-between px-4 py-2.5 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Music className="w-4 h-4 text-gold/70" />
                        <span className="text-white/80 text-sm">Serenade</span>
                      </div>
                      <span className="text-gold text-sm font-bold">$299</span>
                    </Link>
                    
                    <Link
                      to="/choice-mobile"
                      className="flex items-center justify-between px-4 py-2.5 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Crown className="w-4 h-4 text-gold/70" />
                        <span className="text-white/80 text-sm">VIP Milestone</span>
                      </div>
                      <span className="text-gold text-sm font-bold">$450</span>
                    </Link>
                    
                    <div className="border-t border-white/5 my-2"></div>
                    <div className="px-4 py-2">
                      <p className="text-white/40 text-xs uppercase tracking-wider">Transportation</p>
                    </div>
                    
                    <Link
                      to="/shuttle"
                      className="flex items-center justify-between px-4 py-2.5 rounded-lg hover:bg-white/5 transition-colors"
                      data-testid="nav-shuttle"
                    >
                      <div className="flex items-center gap-3">
                        <Truck className="w-4 h-4 text-gold/70" />
                        <span className="text-white/80 text-sm">Shuttle Service</span>
                      </div>
                      <span className="text-blue-400 text-xs">Military Friendly</span>
                    </Link>
                    
                    <div className="border-t border-white/5 my-2"></div>
                    
                    <Link
                      to="/choice-mobile"
                      className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gold/10 hover:bg-gold/20 transition-colors"
                      data-testid="nav-book-now"
                    >
                      <Truck className="w-4 h-4 text-gold" />
                      <span className="text-gold text-sm font-medium">Book Now</span>
                    </Link>
                    
                    <div className="px-4 py-3 mt-2 border-t border-white/5">
                      <div className="flex items-center gap-2 text-white/40 text-xs">
                        <Phone className="w-3 h-3" />
                        <span>(877) 295-5905</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Auth Button */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <Link to="/dashboard">
                <Button 
                  className="btn-luxury bg-gold text-black hover:bg-gold-light rounded-full font-medium px-6"
                  data-testid="nav-dashboard-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/auth">
                  <Button 
                    variant="outline"
                    className="border-white/20 text-white hover:border-gold hover:text-gold rounded-full font-medium px-6"
                    data-testid="nav-signin-btn"
                  >
                    Sign In
                  </Button>
                </Link>
                <Link to="/auth?mode=register">
                  <Button 
                    className="btn-luxury bg-gold text-black hover:bg-gold-light rounded-full font-medium px-6"
                    data-testid="nav-signup-btn"
                  >
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>
          
          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#0A0A0A] border-t border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 py-6 space-y-6">
            {/* Home */}
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-white font-medium"
            >
              Home
            </Link>
            
            {/* Rafha Section */}
            <div>
              <p className="text-gold font-medium mb-3">Rafha Entertainment</p>
              <div className="pl-4 space-y-3 border-l border-white/10">
                <Link
                  to="/vendors"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-1 text-white/70 text-sm"
                >
                  Browse Vendors
                </Link>
                <Link
                  to="/vendor-application"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-1 text-white/70 text-sm"
                >
                  Vendor Application
                </Link>
              </div>
            </div>
            
            {/* Choice Mobile Section */}
            <div>
              <p className="text-gold font-medium mb-3">Choice Mobile</p>
              <div className="pl-4 space-y-3 border-l border-white/10">
                <Link
                  to="/choice-mobile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-1 text-white/70 text-sm"
                >
                  View Packages & Book
                </Link>
                <div className="text-white/40 text-xs pt-2">
                  <Phone className="w-3 h-3 inline mr-1" />
                  (877) 295-5905
                </div>
              </div>
            </div>
            
            <div className="pt-4 border-t border-white/5">
              {user ? (
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-gold text-black hover:bg-gold-light rounded-full font-bold">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-gold text-black hover:bg-gold-light rounded-full font-bold">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
